import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  serviceOrdersService,
  ServiceOrderFilters,
  ServiceOrderCreate,
  ServiceOrderItemCreate,
} from '@/services/serviceOrders';
import { PaymentMethod } from '@/types';

export function useServiceOrders(filters?: ServiceOrderFilters) {
  return useQuery({
    queryKey: ['service-orders', filters],
    queryFn: () => serviceOrdersService.getOrders(filters),
    refetchInterval: 15000, // Refrescar cada 15 segundos
  });
}

interface UsePendingOptions {
  enabled?: boolean;
}

export function usePendingServiceOrders(options?: UsePendingOptions) {
  const { enabled = true } = options || {};
  return useQuery({
    queryKey: ['service-orders', 'pending'],
    queryFn: () => serviceOrdersService.getPendingOrders(),
    refetchInterval: 10000, // Refrescar cada 10 segundos
    enabled,
  });
}

export function useOpenServiceOrders() {
  return useQuery({
    queryKey: ['service-orders', 'open'],
    queryFn: () => serviceOrdersService.getOpenOrders(),
  });
}

export function useServiceOrder(id: number | null) {
  return useQuery({
    queryKey: ['service-order', id],
    queryFn: () => serviceOrdersService.getOrder(id!),
    enabled: !!id,
  });
}

export function useCreateServiceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ServiceOrderCreate) => serviceOrdersService.createOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'], refetchType: 'all' });
    },
  });
}

export function useAddServiceOrderItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, item }: { orderId: number; item: ServiceOrderItemCreate }) =>
      serviceOrdersService.addItem(orderId, item),
    onSuccess: (_, variables) => {
      // Invalidar todas las queries que comiencen con 'service-orders'
      queryClient.invalidateQueries({ queryKey: ['service-orders'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['service-order', variables.orderId] });
    },
  });
}

export function useUpdateServiceOrderItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      itemId,
      data,
    }: {
      orderId: number;
      itemId: number;
      data: { quantity?: number; unit_price?: number; notes?: string };
    }) => serviceOrdersService.updateItem(orderId, itemId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['service-order', variables.orderId] });
    },
  });
}

export function useRemoveServiceOrderItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, itemId }: { orderId: number; itemId: number }) =>
      serviceOrdersService.removeItem(orderId, itemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['service-order', variables.orderId] });
    },
  });
}

export function useMarkServiceOrderReady() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: number) => serviceOrdersService.markReady(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'], refetchType: 'all' });
    },
  });
}

export function useChargeServiceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      paymentMethod,
      paymentReference,
      discountPercentage,
      discountAmount,
      chargeAsCash,
    }: {
      orderId: number;
      paymentMethod?: PaymentMethod;
      paymentReference?: string;
      discountPercentage?: number;
      discountAmount?: number;
      chargeAsCash?: boolean;
    }) => serviceOrdersService.chargeOrder(
      orderId, paymentMethod, paymentReference, discountPercentage, discountAmount, chargeAsCash,
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['sales'], refetchType: 'all' });
    },
  });
}

export function useCancelServiceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: number) => serviceOrdersService.cancelOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'], refetchType: 'all' });
    },
  });
}

export function useDeleteServiceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: number) => serviceOrdersService.deleteOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'], refetchType: 'all' });
    },
  });
}
